import {
  App,
  FEVorvertragsnummer,
  IncludedLeistung,
  Produktlinie,
} from '@ps20-frontend/frontend-models';
import { Fachbaustein, FachbausteinAntrag, FachschluesselTyp, SpartenType } from '@ps20/bff-models';
import dayjs from 'dayjs';

export enum FachschluesselNumber {
  WOHNUNSHAFTPFLICHT = 'HP000098',
  HAFTPFLICHT_HAUPTBAUSTEIN = 'HP000075',
  PFERDEHALTERHAFTPFLICHT = 'HF000027',
  HUNDEHALTERHAFTPFLICHT = 'HU000020',
}

export class UebergreifendItem {
  label: string;
  info: string;
}

export interface Produkt {
  readonly produktlinien: Array<Produktlinie>;
  readonly uebergreifend: Array<UebergreifendItem>;
  readonly spartenType: SpartenType;
  readonly hspLfnr: number;
}

export interface ProduktVnWechsel {
  produktId: App;
  vorvertragsnummer?: FEVorvertragsnummer[];
}

export function selectIndicesOfDeactivatedProduktlinien(state: {
  produkt: Produkt;
}): Array<number> {
  return (
    state.produkt?.produktlinien
      ?.map((value, index) => (value.deactivated ? index : null))
      .filter(index => index != null) || []
  );
}

export function getFachschluessel(produkt: Produkt): Array<FachbausteinAntrag> {
  const fachschluessel: FachbausteinAntrag[] = [];

  const produktlinie = produkt.produktlinien.find(elem => elem.selected);
  produktlinie.leistungen.forEach(leistung => {
    if (leistung.fachschluessel && leistung.writeToEinreichenXML && !leistung.abgewaehlt) {
      const fachbaustein: FachbausteinAntrag = {
        fachschluessel: leistung.fachschluessel,
        leistung: leistung.id,
        fachschluesselTyp: leistung.fachschluesselTyp ?? FachschluesselTyp.REQUIRED,
        reihenfolge: leistung.reihenfolge,
        summenBezeichnung: leistung.summenBezeichnung,
        summe: leistung.summe,
        zusatzFeature: leistung.zusatzFeature,
      };
      fachschluessel.push(fachbaustein);
    }
  });
  produktlinie.gefahren
    .filter(gefahr => gefahr.isSelected)
    .forEach(gefahr => {
      if (gefahr.writeToEinreichenXML) {
        const fachbaustein: FachbausteinAntrag = {
          fachschluessel: gefahr.fachschluessel,
          leistung: gefahr.id,
          taasTarifrisikoschluessel: gefahr.name,
          fachschluesselTyp: FachschluesselTyp.REQUIRED,
          reihenfolge: gefahr.reihenfolge,
          plzFaktor: gefahr.plzFaktor,
          anzahlVorschaeden: gefahr.anzahlVorschaeden,
          deltafaktor: gefahr.deltafaktor,
        };
        fachschluessel.push(fachbaustein);
      }
    });
  produktlinie.zusatzoptionen
    .filter(elem => (elem.isInclusive || elem.isSelected) && elem.writeToEinreichenXML)
    .forEach(elem => {
      const fachbaustein: FachbausteinAntrag = {
        fachschluessel: elem.fachschluessel,
        leistung: elem.id,
        taasTarifrisikoschluessel: elem.name,
        fachschluesselTyp: elem.isInclusive
          ? FachschluesselTyp.REQUIRED
          : FachschluesselTyp.OPTIONAL,
        reihenfolge: elem.reihenfolge,
        includedLeistungen: getBffIncludedLeistungen(elem.includedLeistungen),
        summenBezeichnung: elem.summenBezeichnung,
        plzFaktor: elem.plzFaktor,
        anzahlVorschaeden: elem.anzahlVorschaeden,
        deltafaktor: elem.deltafaktor,
      };
      fachschluessel.push(fachbaustein);
    });

  return fachschluessel;
}

export function getBffIncludedLeistungen(includedLeistungen: IncludedLeistung[]): Fachbaustein[] {
  if (!includedLeistungen) {
    return null;
  }
  return includedLeistungen.map(includedLeistung => {
    return {
      fachschluessel: includedLeistung.fachschluessel,
      textId: includedLeistung.textId,
      leistung: includedLeistung.leistung,
      reihenfolge: includedLeistung.reihenfolge,
      fachschluesselTyp: FachschluesselTyp.REQUIRED,
    };
  });
}

export const ANZAHL_TAGE_TIER_ZU_JUNG = 56; // 8 Wochen

export const isTierZuJungByToday = (geburtsdatumTier: string, dateFormat: string): boolean => {
  return ANZAHL_TAGE_TIER_ZU_JUNG > dayjs().diff(dayjs(geburtsdatumTier, dateFormat), 'days');
};
